import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, errMsg, money, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Truck, XCircle, CheckCircle2, PackageCheck, Send, IndianRupee, Search, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STATUS_TONE = {
  pending: "secondary", confirmed: "outline", packed: "outline", ready: "outline",
  assigned: "outline", out_for_delivery: "outline", delivered: "default",
  cancelled: "destructive", returned: "destructive", refunded: "destructive",
};

const NEXT_STATUS = {
  pending: [{ v: "confirmed", label: "Confirm", icon: CheckCircle2 }, { v: "cancelled", label: "Cancel", icon: XCircle, danger: true }],
  confirmed: [{ v: "packed", label: "Mark packed", icon: PackageCheck }, { v: "cancelled", label: "Cancel", icon: XCircle, danger: true }],
  packed: [{ v: "ready", label: "Ready for pickup", icon: Send }],
  ready: [{ v: "assigned", label: "Assign partner", icon: Truck }],
  assigned: [{ v: "out_for_delivery", label: "Out for delivery", icon: Truck }],
  out_for_delivery: [{ v: "delivered", label: "Delivered", icon: CheckCircle2 }],
};

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [partnerId, setPartnerId] = useState("");
  const [refundOpen, setRefundOpen] = useState(false);
  const [refund, setRefund] = useState({ amount: "", reason: "" });

  // Sprint 31 — searchable partner picker
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnerQuery, setPartnerQuery] = useState("");
  const [selectedPartner, setSelectedPartner] = useState(null);

  // Sprint 32 — searchable picker fetches ALL employees, not just DELIVERY_PARTNER.
  // The backend accepts any user_id for partner_id (only delivery-status updates
  // are gated by role). This matches the admin's mental model — search by any
  // employee name / code and pick whoever the store wants to route the drop to.
  useEffect(() => {
    if (!assignOpen || partners.length > 0) return;
    setPartnersLoading(true);
    api.get("/employees?limit=100")
      .then((r) => setPartners((r.data.items || []).filter((p) => p.is_active !== false)))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setPartnersLoading(false));
  }, [assignOpen, partners.length]);

  const filteredPartners = partners.filter((p) => {
    if (!partnerQuery) return true;
    const q = partnerQuery.toLowerCase();
    return (p.name || "").toLowerCase().includes(q)
        || (p.employee_code || "").toLowerCase().includes(q)
        || (p.email || "").toLowerCase().includes(q)
        || (p.phone || "").toLowerCase().includes(q);
  });

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      api.get(`/invoices/by-order/${id}`).then((r) => setInvoice(r.data)).catch(() => setInvoice(null));
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const transition = async (status) => {
    if (!window.confirm(`Move order to "${status}"?`)) return;
    setBusy(true);
    try { await api.patch(`/orders/${id}/status`, { status }); toast.success(`Status → ${status}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const assign = async () => {
    setBusy(true);
    try { await api.post(`/orders/${id}/assign-delivery`, { partner_id: partnerId }); toast.success("Partner assigned"); setAssignOpen(false); setPartnerId(""); setSelectedPartner(null); setPartnerQuery(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const codConfirm = async () => {
    setBusy(true);
    try { await api.post(`/payments/cod/confirm`, { order_id: id }); toast.success("COD marked captured"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const doRefund = async () => {
    setBusy(true);
    try {
      await api.post(`/payments/refund/${id}`, { amount: Number(refund.amount), reason: refund.reason });
      toast.success("Refund recorded"); setRefundOpen(false); setRefund({ amount: "", reason: "" }); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!order) return <div className="p-8"><Skeleton className="h-8 w-40 mb-4" /><Skeleton className="h-40 w-full" /></div>;

  const nexts = NEXT_STATUS[order.status] || [];
  const canRefund = ["captured", "partially_refunded"].includes(order.payment?.status) || order.payment?.status === "captured";

  return (
    <div className="p-8" data-testid="order-detail-page">
      <Button variant="ghost" size="sm" onClick={() => nav("/admin/orders")} className="mb-4"><ArrowLeft className="w-4 h-4 mr-1" />Back to orders</Button>

      <PageHeader
        title={<span className="font-mono">{order.order_number}</span>}
        subtitle={<><Badge variant={STATUS_TONE[order.status]} className="capitalize mr-2">{order.status.replace(/_/g, " ")}</Badge>{fmtDate(order.created_at)}</>}
        actions={
          <div className="flex gap-2 flex-wrap">
            {nexts.map((n) =>
              n.v === "assigned"
                ? <Button key={n.v} onClick={() => setAssignOpen(true)} disabled={busy} data-testid={`btn-status-${n.v}`}><n.icon className="w-4 h-4 mr-1" />{n.label}</Button>
                : <Button key={n.v} variant={n.danger ? "destructive" : "default"} onClick={() => transition(n.v)} disabled={busy} data-testid={`btn-status-${n.v}`} className={!n.danger ? "btn-primary-glow" : ""}>
                    <n.icon className="w-4 h-4 mr-1" />{n.label}
                  </Button>
            )}
            {order.payment?.method === "cod" && order.payment?.status !== "captured" && (
              <Button variant="outline" onClick={codConfirm} disabled={busy} data-testid="btn-cod-confirm"><IndianRupee className="w-4 h-4 mr-1" />Confirm COD</Button>
            )}
            {canRefund && (
              <Button variant="outline" onClick={() => setRefundOpen(true)} data-testid="btn-refund">Refund</Button>
            )}
          </div>
        }
      />

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 card-soft border-transparent">
          <CardHeader><CardTitle className="font-display text-xl">Items</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {(order.items || []).map((it, i) => (
                <div key={i} className="p-4 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{it.name}</div>
                    <div className="text-xs text-muted-foreground">SKU {it.sku || "—"} · GST {it.gst_rate}%</div>
                  </div>
                  <div className="text-sm text-muted-foreground">{it.qty} × {money(it.price_snapshot)}</div>
                  <div className="w-24 text-right font-semibold">{money(Number(it.price_snapshot) * Number(it.qty))}</div>
                </div>
              ))}
            </div>
            <div className="p-4 space-y-1 text-sm border-t bg-secondary/30">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{money(order.pricing?.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span>{money(order.pricing?.tax)}</span></div>
              {Number(order.pricing?.discount) > 0 && <div className="flex justify-between text-accent"><span>Discount</span><span>- {money(order.pricing?.discount)}</span></div>}
              <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span>{money(order.pricing?.delivery_fee)}</span></div>
              <div className="flex justify-between text-lg font-display font-semibold pt-1 border-t"><span>Total</span><span>{money(order.pricing?.total)}</span></div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="card-soft border-transparent">
            <CardHeader><CardTitle className="font-display text-xl">Delivery address</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-1">
              <div className="font-medium">{order.address?.name}</div>
              <div>{order.address?.phone}</div>
              <div className="text-muted-foreground">{order.address?.line1}</div>
              {order.address?.landmark && <div className="text-muted-foreground">{order.address.landmark}</div>}
              <div className="text-muted-foreground">{order.address?.city}, {order.address?.state} — {order.address?.pincode}</div>
            </CardContent>
          </Card>

          <Card className="card-soft border-transparent">
            <CardHeader><CardTitle className="font-display text-xl">Payment</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Method</span><span className="uppercase">{order.payment?.method}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge variant="outline">{order.payment?.status}</Badge></div>
              {order.payment?.provider_order_id && (
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">RZP order</span><span className="font-mono">{order.payment.provider_order_id.slice(0, 24)}…</span></div>
              )}
              {invoice && (
                <div className="mt-2 pt-2 border-t">
                  <div className="text-xs text-muted-foreground">Invoice</div>
                  <div className="font-mono font-semibold">{invoice.invoice_number}</div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="card-soft border-transparent">
            <CardHeader><CardTitle className="font-display text-xl">Timeline</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-3">
              {(order.timeline || []).map((t, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <div className="w-2 h-2 mt-1.5 rounded-full bg-primary shrink-0" />
                  <div>
                    <div className="font-medium capitalize">{t.status.replace(/_/g, " ")}</div>
                    <div className="text-xs text-muted-foreground">{fmtDate(t.at)}</div>
                    {t.note && <div className="text-xs text-muted-foreground italic">{t.note}</div>}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={assignOpen} onOpenChange={(v) => { setAssignOpen(v); if (!v) { setPartnerQuery(""); setSelectedPartner(null); setPartnerId(""); } }}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Assign delivery partner</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Delivery partner *</Label>
              <div className="mt-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={partnerQuery}
                  onChange={(e) => { setPartnerQuery(e.target.value); if (selectedPartner) { setSelectedPartner(null); setPartnerId(""); } }}
                  placeholder="Search by name, employee code, phone…"
                  className="pl-9"
                  data-testid="partner-search-input"
                />
              </div>

              {selectedPartner ? (
                <div className="mt-2 flex items-center justify-between rounded-lg border bg-primary/5 px-3 py-2" data-testid="partner-selected">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                      {selectedPartner.name || selectedPartner.email}
                      {selectedPartner.role === "DELIVERY_PARTNER"
                        ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 text-[10px] px-1.5 py-0">DP</Badge>
                        : <Badge variant="outline" className="text-[10px] px-1.5 py-0">{selectedPartner.role}</Badge>}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      <span className="font-mono">{selectedPartner.employee_code}</span>
                      {selectedPartner.vehicle_type && <> · {selectedPartner.vehicle_type}{selectedPartner.vehicle_number ? ` · ${selectedPartner.vehicle_number}` : ""}</>}
                      {selectedPartner.phone && <> · {selectedPartner.phone}</>}
                    </div>
                    {selectedPartner.role !== "DELIVERY_PARTNER" && (
                      <div className="text-[10.5px] text-amber-600 mt-1">
                        ⚠ Not a DELIVERY_PARTNER — assignment works but this user can't update delivery status.
                      </div>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedPartner(null); setPartnerId(""); setPartnerQuery(""); }} data-testid="partner-clear">Change</Button>
                </div>
              ) : (
                <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border bg-card">
                  {partnersLoading && <div className="p-4 text-sm text-muted-foreground">Loading employees…</div>}
                  {!partnersLoading && filteredPartners.length === 0 && (
                    <div className="p-4 text-sm text-muted-foreground text-center">
                      No employees match “{partnerQuery}”.
                      <span className="block text-xs mt-1">Try searching by name, code (EMP-…, DP-…) or phone.</span>
                    </div>
                  )}
                  {!partnersLoading && filteredPartners
                    .slice()
                    .sort((a, b) => {
                      // DELIVERY_PARTNERs first, then rest alphabetically
                      const aDP = a.role === "DELIVERY_PARTNER" ? 0 : 1;
                      const bDP = b.role === "DELIVERY_PARTNER" ? 0 : 1;
                      if (aDP !== bDP) return aDP - bDP;
                      return (a.name || "").localeCompare(b.name || "");
                    })
                    .map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => { setSelectedPartner(p); setPartnerId(p.user_id); }}
                        className="w-full text-left px-3 py-2 hover:bg-secondary/60 border-b last:border-b-0 flex items-center justify-between gap-3"
                        data-testid={`partner-option-${p.id}`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate flex items-center gap-2">
                            {p.name || p.email}
                            {p.role === "DELIVERY_PARTNER"
                              ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 text-[10px] px-1.5 py-0">DP</Badge>
                              : <Badge variant="outline" className="text-[10px] px-1.5 py-0">{p.role}</Badge>}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            <span className="font-mono">{p.employee_code}</span>
                            {p.vehicle_type && <> · {p.vehicle_type}{p.vehicle_number ? ` · ${p.vehicle_number}` : ""}</>}
                            {p.delivery_radius_km ? <> · {p.delivery_radius_km} km</> : null}
                            {p.phone ? <> · {p.phone}</> : null}
                          </div>
                        </div>
                        {p.user_id === partnerId && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
                      </button>
                    ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1.5">
                Green DP badges = delivery partners. Manage roles under <span className="font-medium">Employees</span>.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={assign} disabled={busy || !partnerId} className="btn-primary-glow" data-testid="assign-confirm">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent data-testid="refund-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Issue refund</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Amount *</Label><Input type="number" step="0.01" value={refund.amount} onChange={(e) => setRefund({ ...refund, amount: e.target.value })} data-testid="refund-amount" /></div>
            <div><Label>Reason *</Label><Textarea rows={3} value={refund.reason} onChange={(e) => setRefund({ ...refund, reason: e.target.value })} data-testid="refund-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOpen(false)}>Cancel</Button>
            <Button onClick={doRefund} disabled={busy || !refund.amount || !refund.reason} className="btn-primary-glow">Process refund</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
